import _ from "lodash";
import {
  AI_GEN_IDENTIFIER,
  BLANK_ANSWER_ID,
  QUESTION_TYPE_FILL_IN_THE_BLANKS,
  QUESTION_TYPE_MULTIPLE_CHOICE,
  QUESTION_TYPE_TRUE_OR_FALSE,
  NODE_ID,
  QUESTION_ID,
  TITLE_ID,
  ANSWER_TEXT_ID,
  ANSWERS_TEXT_ID,
  RADIO_ID,
  MARKS_ID,
  LEARNING_GOAL_ID,
  SETTINGS_ID,
  CHOICES_ID,
  CHOICE_1,
  CHOICE_2,
  CHOICE_3,
  CHOICE_4,
  BLANKS_ID,
  BLANKS_ANSWER_REPLACEMENT,
  WS_AI_QUESTION_TAGS,
  QUESTION_TYPE_TEXT_RESPONSE,
  INLINE_TEXT_ID,
  QUESTION_TYPE_INLINE_TEXT,
  WS_INSTRUCTION_END_TAG,
  WS_INSTRUCTION_START_TAG,
  PARENT_ID,
} from "Worksheet/modules/WorksheetConstants";
import { generateRandomId, getStyleStrippedText } from "Utils";
import { STREAMING, WRITING } from "Constants/stringConstants";
import { convertMarkdownToHTML } from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/components/AddAssignment/LessonPlanningAssistant/modules/Utils";
import { updateQuestionFieldsInCache } from "Worksheet/modules/WorksheetUtils";
import { getIsWorksheetDocEditorEnabled } from "Worksheet/modules/WorksheetFeatureFlags";
import { sanitizeLatexText } from "@toddle-edu/common-utils";
import html2canvas from "html2canvas";
import katex from "katex";

const {
  ANSWER_TEXT_TAG,
  CHOICES_TAG,
  CORRECT_ANSWER_TAG,
  QUESTION_TEXT_TAG,
  QUESTION_MARK_TAG,
} = WS_AI_QUESTION_TAGS;

// * AI SUPPORTED QUESTION LABELS
export const AI_SUPPORTED_QUESTION_LABELS = [
  QUESTION_TYPE_FILL_IN_THE_BLANKS,
  QUESTION_TYPE_MULTIPLE_CHOICE,
  QUESTION_TYPE_TRUE_OR_FALSE,
  QUESTION_TYPE_TEXT_RESPONSE,
  QUESTION_TYPE_INLINE_TEXT,
];

export const TYPE_OF_QUESTION_TAG = WS_AI_QUESTION_TAGS.TYPE_OF_QUESTION_TAG;

/**
 * @deprecated Use `generateFieldSetV2` instead.
 * This is kept for backward compatibility.
 * It will be removed in future versions.
 */
/**
 * Generates a set of unique field IDs for various question components.
 * Each field ID is prefixed with the AI_GEN_IDENTIFIER and a randomly generated ID.
 *
 * @returns {Object} - An object containing unique field IDs for question components.
 */
export const generateFieldSet = () => {
  const FIELD_NAMES = [
    NODE_ID,
    QUESTION_ID,
    TITLE_ID,
    ANSWER_TEXT_ID,
    ANSWERS_TEXT_ID,
    RADIO_ID,
    MARKS_ID,
    LEARNING_GOAL_ID,
    SETTINGS_ID,
    CHOICES_ID,
    CHOICE_1,
    CHOICE_2,
    CHOICE_3,
    CHOICE_4,
    BLANKS_ID,
    BLANK_ANSWER_ID,
    INLINE_TEXT_ID,
  ];

  return _.mapValues(
    _.keyBy(FIELD_NAMES),
    () => `${AI_GEN_IDENTIFIER}${generateRandomId()}`
  );
};

/**
 * Generates a set of unique field IDs for various question components.
 * Each field ID is prefixed with the AI_GEN_IDENTIFIER and a randomly generated ID.
 *
 * @returns {Object} - An object containing unique field IDs for question components.
 */
export const generateFieldSetV2 = () => {
  const FIELD_NAMES = [
    PARENT_ID,
    QUESTION_ID,
    TITLE_ID,
    ANSWER_TEXT_ID,
    ANSWERS_TEXT_ID,
    RADIO_ID,
    MARKS_ID,
    LEARNING_GOAL_ID,
    SETTINGS_ID,
    CHOICES_ID,
    CHOICE_1,
    CHOICE_2,
    CHOICE_3,
    CHOICE_4,
    BLANKS_ID,
    BLANK_ANSWER_ID,
    INLINE_TEXT_ID,
  ];

  return _.mapValues(
    _.keyBy(FIELD_NAMES),
    () => `${AI_GEN_IDENTIFIER}${generateRandomId()}`
  );
};

/**
 * Creates a field object with the given id, uid, and value.
 *
 * @param {string} id - The unique identifier for the field.
 * @param {string} uid - The unique identifier for the user.
 * @param {string} value - The value of the field.
 * @returns {Object} - The created field object.
 */
const createField = (id, uid, value) => ({
  id,
  uid,
  value,
  resolveFields: {
    id,
    type: "UNDEFINED_ENTITY",
    __typename: "UndefinedEntity",
  },
  resolvedMinimalTree: {
    __typename: "UndefinedEntity",
  },
  __typename: "QuestionFields",
});

/**
 * Formats an input string into an array of segments separated by specific characters,
 * respecting quoted segments enclosed within double quotes.
 *
 * @param {string} input - The input string to be formatted
 * @returns {Array} - Array of formatted segments extracted from the input string
 *
 * eg:-
 * input "[["data1"],["data2"]]"
 * output ["data1","data2"]
 */
const formateFillInTheBlanks = input => {
  const result = [];
  let current = "";
  let insideQuotes = false;

  _.forEach(input, char => {
    if (_.isEqual(char, '"')) {
      insideQuotes = !insideQuotes;
    } else if (_.includes(["[", "]", ","], char) && !insideQuotes) {
      if (!_.isEmpty(_.trim(current))) {
        result.push(_.trim(current));
      }
      current = "";
    } else {
      current += char;
    }
  });

  if (!_.isEmpty(_.trim(current))) {
    result.push(_.trim(current));
  }

  return result;
};

/**
 * Replaces all forward slashes in URLs of markdown image tags with "-SLASH-" to prevent parsing issues.
 *
 * This function identifies Markdown image syntax (![alt](url)) in the provided content,
 * then modifies each image URL by replacing all '/' characters with '-SLASH-'.
 * This prevents the slashes from being incorrectly processed during Markdown parsing.
 * The function also handles and removes any query parameters from the URLs.
 *
 * @param {string} markdownContent - The markdown content containing image tags to process
 * @returns {string} The modified markdown content with replaced image URLs
 */
const replaceImageUrlSlashes = markdownContent => {
  if (!markdownContent || !_.isString(markdownContent)) return markdownContent;

  // Regular expression to match Markdown image syntax with optional closing parenthesis
  const imageRegex = /!\[([\s\S]*?)\]\(([^)\s]+)(?:\?[^)\s]*)?/g;

  return _.replace(markdownContent, imageRegex, (match, altText, url) => {
    // Remove query parameters if present
    const baseUrl = url.split("?")[0];
    // Replace all '/' with '-SLASH-'
    const modifiedUrl = _.replace(baseUrl, /\//g, "-SLASH-");
    // Reconstruct the Markdown image syntax with the modified URL
    return `![${altText}](${_.trimEnd(modifiedUrl, ")")})`;
  }).replace("))", ")");
};

/**
 * Parses an input string containing tags and content, and returns an array of objects
 * representing the parsed content.
 *
 * @param {string} inputString - The string to parse, containing tags and content.
 * @returns {Array} - An array of objects with parsed content.
 *
 * eg-> https://coda.io/d/2A-Team-Internal-Doc_d7roekmmvlu/FE-utils_suOqZ#_luoTD
 */
export const parseData = _inputString => {
  let insideTag = false; // Indicates if the parser is currently inside a tag
  let currentTag = ""; // Holds the current tag name
  let currentContent = ""; // Holds the current content within a tag
  const tempOutput = []; // Temporary array to hold the output objects

  // Remove backticks and encode image URLs in markdown format
  const inputString = replaceImageUrlSlashes(_inputString);

  /**
   * Processes the current content based on the tag and pushes the parsed object to tempOutput.
   *
   * @param {string} tag - The current tag name.
   * @param {string} content - The content to process within the tag.
   */
  const processContent = (tag, content) => {
    const trimmedContent = _.trim(content);
    if (_.isEqual(tag, CHOICES_TAG)) {
      // Split the content by newline, trim each choice, and remove starting '--' prefix
      const choicesArray = _.chain(trimmedContent)
        .split("\n")
        .map(choice => _.trim(choice).replace(/^--/, "")) // Remove starting '--' prefix
        .value();
      tempOutput.push({ [tag]: choicesArray });
    } else {
      // Replace #BLANK# with {} and add the processed content to tempOutput
      let replacedContent = _.replace(
        trimmedContent,
        /#BLANK#/g,
        BLANKS_ANSWER_REPLACEMENT
      );

      // Only apply newline consolidation on content not inside triple backticks
      if (!_.includes(replacedContent, "```")) {
        replacedContent = _.replace(replacedContent, /(\s*\n\s*)+/g, "\n");
      } else {
        // For content with code blocks, process carefully preserving code blocks
        const codeBlocks = [];
        const processedContent = replacedContent.replace(
          /```[\s\S]*?```/g,
          match => {
            codeBlocks.push(match);
            return `__CODE_BLOCK_${codeBlocks.length - 1}__`;
          }
        );

        // Apply newline consolidation on regular content
        const processedResult = _.replace(
          processedContent,
          /(\s*\n\s*)+/g,
          "\n"
        );

        // Restore code blocks
        replacedContent = processedResult.replace(
          /__CODE_BLOCK_(\d+)__/g,
          (_, index) => codeBlocks[parseInt(index, 10)]
        );
      }

      tempOutput.push({ [tag]: `\`${replacedContent}\`` });
    }
  };

  _.forEach(inputString, char => {
    if (_.isEqual(char, "<")) {
      insideTag = true;
      if (!_.isEmpty(_.trim(currentContent))) {
        processContent(currentTag, currentContent);
        currentContent = ""; // Reset current content after processing
      }
      currentTag = ""; // Start a new tag
    } else if (_.isEqual(char, ">")) {
      insideTag = false;
    } else if (insideTag && !_.isEqual(char, "/")) {
      currentTag += char; // Build the tag name
    } else {
      if (!insideTag && !_.isEqual(char, "/")) {
        currentContent += char; // Build the content within the tag
      }
    }
  });

  // Push the last collected content
  if (!_.isEmpty(currentTag) && !_.isEmpty(_.trim(currentContent))) {
    processContent(currentTag, currentContent);
  }

  return tempOutput;
};

/**
 * @param {*} element
 * @returns class name for the element based on whether Worksheet Doc Editor is enabled or not
 */
const getClassNameForElement = element => {
  const showDocEditor = getIsWorksheetDocEditorEnabled();
  switch (element) {
    case "p":
      return showDocEditor ? "ds-de-paragraph" : "tde-paragraph";
    case "table":
      return showDocEditor ? "ds-de-table" : "tde-table";
    case "td":
      return showDocEditor ? "ds-de-tableCell" : "tde-tableCell";
    default:
      return "";
  }
};

/**
 * Replaces placeholders in the input string with values from the provided array.
 * Placeholders are denoted by "{}" and are replaced sequentially by corresponding values from the array.
 *
 * @param {string} inputStr - The input string containing placeholders
 * @param {Array} values - The array of values to replace the placeholders
 * @returns {string} - The resulting string with placeholders replaced by values
 */
const replacePlaceholders = (inputStr, values) => {
  let valueIndex = 0; // Initialize the index to track the current value from the array
  return _.replace(inputStr, /{}/g, () => {
    // Check if there are still values available in the array
    if (valueIndex < _.size(values)) {
      // Replace the placeholder with the current value and increment the index
      return `{${_.get(values, valueIndex++)}}`;
    }
    // If no more values are available, leave the placeholder as is
    return "{}";
  });
};

const updateContentWithCodeBlock = content => {
  let string = content;

  // Extract content between <pre> and </pre> tags
  // Extract content between <pre> tags
  let preTagContents = [];
  string = _.replace(
    string,
    /<pre[^>]*>([\s\S]*?)<\/pre>/gi,
    (match, content) => {
      preTagContents.push(content);
      return `__PRE_TAG_PLACEHOLDER_${preTagContents.length - 1}__`;
    }
  );

  // Process the extracted pre tag contents
  preTagContents = _.map(preTagContents, content => {
    // Remove code tags and prepare content
    const contentWithoutCode = _.replace(
      _.replace(content, /<code\b[^>]*>/gi, ""),
      /<\/code>/gi,
      ""
    );

    // Split by lines and wrap each in styled span
    return _.chain(contentWithoutCode)
      .split("\n")
      .map(
        line =>
          `<span class="token-black" style="white-space:pre-wrap">${line}</span>`
      )
      .join("\n")
      .value();
  });

  // Restore the pre blocks with processed content
  string = _.reduce(
    preTagContents,
    (result, content, index) => {
      return _.replace(
        result,
        `__PRE_TAG_PLACEHOLDER_${index}__`,
        `<pre class='' style="margin: 8px 0px; padding: 8px 16px; white-space:pre-wrap; font-size: 16px;">${content}</pre>`
      );
    },
    string
  );

  // Fix nested pre tags inside paragraph tags
  string = _.replace(
    string,
    /<p([^>]*)>([\s\S]*?)<pre([^>]*)>([\s\S]*?)<\/pre>([\s\S]*?)<\/p>/gi,
    (match, pAttrs, before, preAttrs, preContent, after) => {
      let result = "";
      if (!_.isEmpty(_.trim(before))) {
        result += `<p${pAttrs}>${before}</p>`;
      }
      result += `<pre${preAttrs}>${preContent}</pre>`;
      if (!_.isEmpty(_.trim(after))) {
        result += `<p${pAttrs}>${after}</p>`;
      }
      return result;
    }
  );

  // Handle standalone code tags outside pre blocks
  const preservedPreBlocks = [];
  string = _.replace(string, /<pre[\s\S]*?<\/pre>/gi, match => {
    preservedPreBlocks.push(match);
    return `__PRESERVED_PRE_BLOCK_${preservedPreBlocks.length - 1}__`;
  });

  // Replace code tags with their raw content
  string = _.replace(
    string,
    /<code\b[^>]*>([\s\S]*?)<\/code>/gi,
    (match, codeContent) => codeContent
  );

  // Restore preserved pre blocks
  _.forEach(preservedPreBlocks, (block, index) => {
    string = _.replace(string, `__PRESERVED_PRE_BLOCK_${index}__`, block);
  });

  string = _.replace(string, /`/g, "");

  return string;
};

/**
 *
 * @param {*} htmlString
 * @returns html string
 */
const convertLatexToHTML = htmlString => {
  if (!htmlString) return "";

  // Find content between any number of dollars and remove all starting and ending dollars
  const latexRegex = /\$+([^$]+)\$+/g;

  const renderLatexContent = (match, latexContent) => {
    // Replace #UNDERSCORE# with actual underscore and sanitize the content
    let processedLatexContent = _.trim(latexContent);
    processedLatexContent = processedLatexContent
      .replace(/^\$+|\$+$/g, "")
      .trim();
    processedLatexContent = _.replace(
      processedLatexContent,
      /#UNDERSCORE#/g,
      "_"
    );
    processedLatexContent = _.trimEnd(processedLatexContent, "\\");
    const paragraphClassName = getClassNameForElement("p");
    processedLatexContent = _.replace(
      processedLatexContent,
      new RegExp(`<p class="${paragraphClassName}">`, "g"),
      "_"
    );
    processedLatexContent = _.replace(processedLatexContent, /<\/p>/g, "");

    try {
      const katexHTML = katex.renderToString(
        sanitizeLatexText(processedLatexContent),
        {
          displayMode: false,
          throwOnError: false,
        }
      );

      // * Removing katex error colors
      const updatedKatexHTML = _.replace(
        katexHTML,
        /color: rgb\(204, 0, 0\);/g,
        ""
      ).replace(/#cc0000/g, '"inherit"');

      return `<span style="display: inline; vertical-align: middle; font-size: 14px;">${updatedKatexHTML}</span>`;
    } catch (e) {
      return match;
    }
  };

  const updatedString = _.replace(htmlString, latexRegex, renderLatexContent);
  return _.replace(updatedString, /\${2,}/g, "");
};

/**
 *
 * @param {*} mdContent
 * @returns html content
 */
export const convertMdToHTML = (content, shouldReplaceDollars = true) => {
  if (!content || !_.isString(content)) return "";
  let parsedContent = content; // _.replace(content, /`/g, "");

  if (_.startsWith(parsedContent, "`") && !_.startsWith(parsedContent, "```")) {
    parsedContent = _.trimStart(parsedContent, "`", "");
  }

  if (_.endsWith(parsedContent, "`") && !_.endsWith(parsedContent, "```")) {
    parsedContent = _.trimEnd(parsedContent, "`", "");
  }

  try {
    // Replace markdown image patterns ![ALT_TEXT](image_url) with HTML image tags
    parsedContent = _.replace(parsedContent, /-SLASH-/g, "/");

    // Check for markdown images with closing brackets in alt text and fix them
    // Pattern: ![some_content]] -> ![some_content\]]
    // Check for markdown images with double closing brackets in alt text and fix them
    // Pattern: ![some_content]] -> ![some_content]
    parsedContent = _.replace(
      parsedContent,
      /!\[(.*?)]\]/g,
      (match, altText) => `![${altText}]`
    );

    // * Replace underscores with LATEX_UNDERSCORE only within LaTeX expressions to prevent it from being interpreted as a subscript
    parsedContent = _.replace(
      parsedContent,
      /\$+([^$]+)\$+/g,
      (match, latexContent) => {
        const processedLatex = _.replace(latexContent, /_/g, "#UNDERSCORE#");
        return match.replace(latexContent, processedLatex);
      }
    );

    // * Adding to make sure if
    parsedContent = `${parsedContent}$$`;

    parsedContent = convertMarkdownToHTML({
      content: parsedContent,
      shouldParseContent: true,
    });
    // * Stripping the content of any style tags
    parsedContent = _.trim(parsedContent);
    parsedContent = getStyleStrippedText({
      text: parsedContent,
      type: "DOCUMENT_EDITOR",
    });

    // Replace all <code> tags with <span> tags using lodash and regex
    parsedContent = updateContentWithCodeBlock(parsedContent);

    // Replace all <th> tags with <td> tags using lodash and regex
    parsedContent = _.replace(parsedContent, /<th>/g, "<td>");
    // Replace all </th> tags with </td> tags using lodash and regex
    parsedContent = _.replace(parsedContent, /<\/th>/g, "</td>");

    // Replace table tags with styled tables using lodash and regex
    const tableClassName = getClassNameForElement("table");
    parsedContent = _.replace(
      parsedContent,
      /<table>/g,
      `<table class="${tableClassName}" style="border-collapse:separate;border-spacing:0;border-radius:5px;overflow:hidden;margin:12px 0px 24px 0px;border:1px solid #d3d3d3">`
    );

    // Replace td tags with styled cells using lodash and regex
    const tableCellClassName = getClassNameForElement("td");
    parsedContent = _.replace(
      parsedContent,
      /<td>/g,
      `<td class="${tableCellClassName}" style="padding:8px;border-top-left-radius:4px;width:330px;border-right:1px solid #d3d3d3;border-bottom:1px solid #d3d3d3;vertical-align:top;text-align:start">`
    );

    // Replace all newlines with <br /> tags
    // Replace newlines with <p> tags, but preserve them inside <pre> and <code> tags
    const preCodeBlocks = [];
    let tempContent = parsedContent.replace(
      /<pre[\s\S]*?<\/pre>|<code[\s\S]*?<\/code>/gi,
      match => {
        preCodeBlocks.push(match);
        return `__PRE_CODE_BLOCK_${preCodeBlocks.length - 1}__`;
      }
    );

    const paragraphClassName = getClassNameForElement("p");

    // Apply newline replacement only to content outside preserved blocks
    tempContent = _.replace(
      tempContent,
      /\n/g,
      `<p class="${paragraphClassName}"></p>`
    );

    // Restore the preserved <pre> and <code> blocks
    parsedContent = tempContent.replace(
      /__PRE_CODE_BLOCK_(\d+)__/g,
      (_, index) => preCodeBlocks[parseInt(index, 10)]
    );

    // Replace all <p> tags with <p class="paragraphClassName"> tags
    parsedContent = _.replace(
      parsedContent,
      /<p>/g,
      `<p class="${paragraphClassName}">`
    );

    parsedContent = _.replace(
      parsedContent,
      /<em>/g,
      `<p class="${paragraphClassName}">`
    );

    parsedContent = _.replace(parsedContent, /<\/em>/g, "</p>");

    // * Updating latex with HTML
    parsedContent = convertLatexToHTML(parsedContent);
  } catch (error) {
    parsedContent = content;
  }

  return parsedContent;
};

/**
 * Process key-value pairs to update question fields based on key and question type.
 *
 * @param {string} key - The key indicating the type of field to process.
 * @param {any} value - The value associated with the key.
 * @param {object} fieldIds - Object containing field IDs for creating fields.
 * @param {string} questionType - Type of question ("Fill in the blanks", "True or false", "Multiple choice").
 * @param {array} arr - Additional array used for processing.
 * @param {boolean} isQueriesV2Enabled - Flag to determine if queries v2 is enabled.
 * @returns {void} - Updates question fields in cache based on the processed key-value pairs
 */
export const processKey = (
  key,
  value,
  fieldIds,
  questionType,
  arr,
  isQueriesV2Enabled = false
) => {
  let fields = [];

  switch (key) {
    case QUESTION_TEXT_TAG:
      if (_.isEqual(questionType, QUESTION_TYPE_FILL_IN_THE_BLANKS)) {
        const correctAnswer = {
          answers: [],
          fullField: _.replace(value, /`/g, ""),
        };

        fields = [
          createField(fieldIds.blankAnswerId, "blanksAnswers", correctAnswer),
          createField(fieldIds.blanksId, "blanks", _.replace(value, /`/g, "")),
        ];
      } else {
        if (questionType === QUESTION_TYPE_INLINE_TEXT) {
          // * Adding inline text field
          const paragraphClassName = getClassNameForElement("p");
          const parsedValue = convertMdToHTML(value);
          fields = [
            createField(
              fieldIds.inlineTextId,
              "title",
              `<p class="${paragraphClassName}">${parsedValue}</p>`
            ),
          ];
        } else {
          const parsedTitle = convertMdToHTML(value);
          fields = [createField(fieldIds.titleId, "title", parsedTitle)];
        }
      }
      break;

    case ANSWER_TEXT_TAG:
      fields = [
        createField(fieldIds.answerTextId, "answer", convertMdToHTML(value)),
      ];
      break;

    case CHOICES_TAG:
      fields = [
        createField(
          fieldIds.choicesId,
          "choices",
          _.map(value, (val, index) => ({
            id: fieldIds[`choice${index + 1}`],
            value: convertMdToHTML(val),
          }))
        ),
      ];
      break;

    case CORRECT_ANSWER_TAG:
      if (_.isEqual(questionType, QUESTION_TYPE_FILL_IN_THE_BLANKS)) {
        const questionText = _.get(arr, `[1].${QUESTION_TEXT_TAG}`, "").replace(
          /`/g,
          ""
        );
        const answerArray = formateFillInTheBlanks(_.replace(value, /`/g, ""));
        const correctAnswer = {
          answers: answerArray,
          fullField: replacePlaceholders(questionText, answerArray),
        };

        fields = [
          createField(fieldIds.blankAnswerId, "blanksAnswers", correctAnswer),
        ];
      } else if (_.isEqual(questionType, QUESTION_TYPE_TRUE_OR_FALSE)) {
        const correctAnswer = _.replace(value, /`/g, "").toLowerCase();
        fields = [
          createField(fieldIds.radioId, "radio", { correct: correctAnswer }),
        ];
      } else if (_.isEqual(questionType, QUESTION_TYPE_MULTIPLE_CHOICE)) {
        const correctAnswer = parseInt(value.replace(/`/g, ""));
        const val = [
          { id: fieldIds.choice1, isSelected: false },
          { id: fieldIds.choice2, isSelected: false },
          { id: fieldIds.choice3, isSelected: false },
          { id: fieldIds.choice4, isSelected: false },
        ];

        if (
          _.isNumber(correctAnswer) &&
          correctAnswer >= 0 &&
          correctAnswer < val.length
        ) {
          val[correctAnswer].isSelected = true;
        }
        fields = [createField(fieldIds.answersTextId, "answers", val)];
      }
      break;

    case QUESTION_MARK_TAG:
      {
        const marks = _.replace(value, /`/g, "");
        if (_.isNumber(marks)) {
          fields = [createField(fieldIds.marksId, "marks", marks)];
        }
      }
      break;
  }

  if (_.size(fields) > 0) {
    // * Updating question field in cache
    updateQuestionFieldsInCache({ fields, isQueriesV2Enabled });
  }
};

/**
 * @deprecated Use `getNonCachedLastQuestionIdV2` instead.
 * This is kept for backward compatibility.
 * It will be removed in future versions.
 */
export const getNonCachedLastQuestionId = lastSection => {
  //extract last Question node id by filtering ai generated cached id
  const questions = _.get(lastSection, "childrenV2.edges", []);
  const nodeIds = _.map(questions, "node.id", []);
  const itemId = _.map(questions, "node.item.id", []);
  const filteredNodeIds = _.filter(
    nodeIds,
    id => !_.includes(id, AI_GEN_IDENTIFIER)
  );
  const filteredItemIds = _.filter(
    itemId,
    id => !_.includes(id, AI_GEN_IDENTIFIER)
  );
  const lastNodeId = _.last(filteredNodeIds, null);
  const lastQuestionId = _.last(filteredItemIds, null);

  return { lastNodeId, lastQuestionId };
};

/**
 * Retrieves the last non-cached question ID from the given section.
 *
 * @param {Object} lastSection - The section object containing questions.
 * @returns {{ lastQuestionId: string | undefined }} An object with the last non-cached question ID.
 */
export const getNonCachedLastQuestionIdV2 = lastSection => {
  const questions = _.get(lastSection, "children", []);
  const itemId = _.map(questions, "item.id", []);
  const filteredItemIds = _.filter(
    itemId,
    id => !_.includes(id, AI_GEN_IDENTIFIER)
  );
  const lastQuestionId = _.last(filteredItemIds);

  return { lastQuestionId };
};

/**
 *
 * @param {*} currentAIState
 * @returns { isStreaming, isEnd, isStreamingOrEnd }
 */
export const getCurrentAIState = currentAIState => {
  const isStreaming = _.isEqual(currentAIState, STREAMING);
  const isEnd = _.isEqual(currentAIState, "END");
  const isWritting = _.isEqual(currentAIState, WRITING);
  const isStreamingOrEnd = isStreaming || isEnd;

  return { isStreaming, isEnd, isStreamingOrEnd, isWritting };
};

// * Handle Metric and Meta Data
// * for AI Assessment
// * and Debug Info
export const handleMetricAndMetaInitials = ({
  messages,
  aiConversationId,
  updateAIConversation,
  conversationMetadata,
  updateAIAssessmentTools,
  metadataController,
}) => {
  const updatedMessage = _.filter(messages, message =>
    _.isEqual(_.get(message, "node.authorType"), "assistant")
  );

  const messageNode = _.get(_.last(updatedMessage), "node", {});
  const content = _.get(messageNode, "content", "");
  const usageMetricsId = _.get(messageNode, "usageMetricsId", "");
  const metaData = _.get(messageNode, "metadata", {});

  const {
    step,
    stepOneMetricId,
    stepTwoMetricId,
    setStepOneMetricId,
    setStepTwoMetricId,
  } = metadataController;
  if (step == 1 && !stepOneMetricId && !_.isEmpty(usageMetricsId) && metaData) {
    // * Set Step 1 Metric ID and Meta Data, PLAN CREATION STEP
    setStepOneMetricId(usageMetricsId);
    updateAIAssessmentTools({
      preStepDebugInfoMetaData: {
        [aiConversationId]: { usageMetricsId, metaData },
      },
    });
    updateAIConversation({
      input: {
        id: aiConversationId,
        metadata: {
          ...conversationMetadata,
          preStepDebugInfo: { usageMetricsId, metaData },
        },
      },
    });
  } else if (
    metaData &&
    step == 2 &&
    !stepTwoMetricId &&
    !_.isEmpty(usageMetricsId) &&
    usageMetricsId !== stepOneMetricId
  ) {
    // * Set Step 2 Metric ID and Meta Data, QUESTION CREATION STEP
    setStepTwoMetricId(usageMetricsId);
    updateAIAssessmentTools({
      debugInfoMetaData: {
        [aiConversationId]: { usageMetricsId, metaData },
      },
    });
    updateAIConversation({
      input: {
        id: aiConversationId,
        metadata: {
          ...conversationMetadata,
          debugInfo: { usageMetricsId, metaData },
        },
      },
    });
  }

  return { content, usageMetricsId };
};

const WORKSHEET_PLAN_START_TAG = "<wp>";
const WORKSHEET_PLAN_END_TAG = "</wp>";
const WORKSHEET_QUESTION_START_TAG = "<wq>";
const WORKSHEET_QUESTION_END_TAG = "</wq>";
const WORKSHEET_VISUALIZATION_START_TAG = "<vs>";
const WORKSHEET_VISUALIZATION_END_TAG = "</vs>";

/**
 * Extracts content between worksheet question tags from a string.
 *
 * @param {string} content - The string containing worksheet question data
 * @returns {string} - The extracted content between tags or empty string if not found
 */
export const extractWorksheetQuestionContent = content => {
  if (!_.includes(content, WORKSHEET_QUESTION_END_TAG)) return "";

  let data = _.split(content, WORKSHEET_QUESTION_START_TAG);
  data = _.last(data);
  data = _.split(data, WORKSHEET_QUESTION_END_TAG);
  return _.first(data);
};

export const extractWorksheetVisualizationContent = content => {
  if (!_.includes(content, WORKSHEET_VISUALIZATION_END_TAG)) return "";

  let data = _.split(content, WORKSHEET_VISUALIZATION_START_TAG);
  data = _.last(data);
  data = _.split(data, WORKSHEET_VISUALIZATION_END_TAG);
  return _.first(data);
};

export const getWorksheetQuestionGeneratePromptOneResponse = content => {
  return {
    isStart: _.includes(content, WORKSHEET_PLAN_START_TAG),
    isPlanReceived: _.includes(content, WORKSHEET_PLAN_END_TAG),
    isQuestionReceived: _.includes(content, WORKSHEET_QUESTION_END_TAG),
    isVisualizationReceived: _.includes(
      content,
      WORKSHEET_VISUALIZATION_END_TAG
    ),
    value: extractWorksheetQuestionContent(content),
    visualizationValue: extractWorksheetVisualizationContent(content),
  };
};

/**
 *
 * @param {} callback
 * WORKSHEET INSTRUCTION FROM AI
 */
/**
 * Loads MathLive scripts and returns a Promise that resolves when loading is complete.
 * This function handles potential conflicts with Monaco editor's AMD loader.
 *
 * @returns {Promise} A promise that resolves when the MathLive script is loaded
 */
const existingScript = () => document.getElementById("mathlive-script");
const loadMathLiveScripts = () => {
  return new Promise(resolve => {
    const isScriptLoaded = existingScript();

    if (!isScriptLoaded) {
      const script = document.createElement("script");
      script.src = "/mathlive/mathlive-0.107.0.min.js";
      script.id = "mathlive-script";
      script.defer = true;

      /* 
        Required to make mathlive work with monaco-editor. 
        monaco-editor uses custom AMD loader which conflicts with 
        mathlive script and breaks equation editor.
      */
      const originalDefine = window.define; // define from monaco-editor if loaded previously
      window.define = undefined; // Temporarily disable AMD loader

      script.onload = () => {
        window.define = originalDefine; // Restore AMD loader
        resolve();
      };

      document.head.appendChild(script);
    } else {
      resolve();
    }
  });
};

/**
 * Replaces LaTeX expressions in text with corresponding image representations.
 *
 * @param {string} text - The text containing LaTeX expressions
 * @param {Function} uploadFileV2 - Function to upload files
 * @returns {string} - Text with LaTeX expressions replaced by image tags
 */
const replaceLatexWithImages = async (text, uploadFileV2) => {
  // Return early if text is invalid or upload function is missing
  if (!text || !_.isString(text) || !uploadFileV2) return text;

  let processedText = text;
  let mathLiveContainer = null;
  const latexBlobs = [];

  try {
    // Load MathLive scripts
    await loadMathLiveScripts();

    const LATEX_REGEX = /\$\$(.*?)\$\$/g;
    const latexMatches = text.match(LATEX_REGEX);

    // Return original text if no LaTeX expressions found
    if (!latexMatches || _.isEmpty(latexMatches)) return text;

    // Create a hidden MathLive field container for rendering
    mathLiveContainer = document.createElement("math-field");
    mathLiveContainer.id = "temp-mathfield-for-images";
    mathLiveContainer.style.position = "fixed";
    mathLiveContainer.style.left = "-9999px";
    mathLiveContainer.style.top = "0";
    mathLiveContainer.style.opacity = "0";
    mathLiveContainer.style.pointerEvents = "none";
    document.body.appendChild(mathLiveContainer);

    // Process each LaTeX expression sequentially
    for (let i = 0; i < latexMatches.length; i++) {
      const latexMatch = latexMatches[i];
      const latexContent = _.trim(latexMatch.replace(/\$\$/g, ""));
      if (!latexContent || !mathLiveContainer) continue;

      mathLiveContainer.value = latexContent;
      if (typeof mathLiveContainer.setValue === "function") {
        mathLiveContainer.setValue(latexContent);
      }

      // * Wait for the MathLive field to render
      await new Promise(resolve => setTimeout(resolve, 100));

      /**
       * Query selector for the MathLive field container.
       * This is used to access the rendered equation inside the shadow DOM.
       * The shadow DOM is used to encapsulate the styles and structure of the MathLive field.
       */
      const SHADOW_ROOT_QUERY_SELECTOR = ".ML__fieldcontainer__field";
      if (!mathLiveContainer.shadowRoot) continue;
      mathLiveContainer.focus();
      const mathLiveFieldContainer = mathLiveContainer.shadowRoot.querySelector(
        SHADOW_ROOT_QUERY_SELECTOR
      );
      if (
        !mathLiveFieldContainer ||
        !mathLiveFieldContainer.children ||
        mathLiveFieldContainer.children.length < 1
      ) {
        continue;
      }

      const mathLiveField = mathLiveFieldContainer.children[0];
      if (!mathLiveField) continue;

      mathLiveField.style.width = "auto";

      /**
       * Get the bounding rectangle of the MathLive field.
       * This is used to determine the dimensions of the rendered equation.
       * The bounding rectangle provides information such as height, width, and position.
       */
      const rect = mathLiveField.getBoundingClientRect();
      if (!rect || !rect.height || !rect.width) continue;
      const { height, width, bottom } = rect;
      let verticalAlign = 0;
      const strutElement = mathLiveField.querySelector(".ML__strut");
      if (strutElement) {
        const strutRect = strutElement.getBoundingClientRect();
        if (strutRect && strutRect.bottom) {
          verticalAlign = -(bottom - strutRect.bottom) + 1;
        }
      }

      /**
       * Use html2canvas to render the MathLive field as a canvas.
       * The canvas is then converted to a blob (binary large object) representing the image.
       * The blob is used to create an image file for uploading.
       */
      const canvas = await html2canvas(mathLiveField, {
        height: height,
        width: width,
        backgroundColor: null,
        logging: false,
        scale: 5,
        useCORS: true,
        allowTaint: true,
      });

      if (!canvas) continue;
      const blob = await new Promise(r => canvas.toBlob(r, "image/png", 1));
      if (blob)
        latexBlobs.push({
          latex: latexMatch,
          blob,
          height,
          width,
          verticalAlign,
        });
    }
  } catch (error) {
    return text;
  } finally {
    if (mathLiveContainer && document.body.contains(mathLiveContainer)) {
      document.body.removeChild(mathLiveContainer);
    }
  }

  /**
   * Upload LaTeX blobs to the server and replace them in the text.
   * This is done in a try-catch block to handle any errors during the upload process.
   * The uploadFileV2 function is expected to return a URL for the uploaded image.
   * The uploaded image URL is then used to replace the LaTeX expression in the text.
   */
  try {
    if (latexBlobs.length > 0) {
      const uploadPromises = latexBlobs.map(
        async ({ latex, blob, height, width, verticalAlign }) => {
          try {
            const id = generateRandomId();
            const url = await uploadFileV2({
              file: new File([blob], "id", {
                type: "image/png",
              }),
              status: "INSERTING",
              attachment: {
                type: "EQ",
                mimeType: "image/png",
                metadata: {
                  fileExtension: "png",
                  fileGroup: "content/eq",
                },
              },
              uploadId: id,
              hideToastProgress: true,
            });

            return { latex, url, height, width, verticalAlign, success: true };
          } catch (uploadError) {
            return { latex, success: false, error: uploadError };
          }
        }
      );

      const uploadResults = await Promise.allSettled(uploadPromises);

      uploadResults.forEach(result => {
        if (
          result &&
          result.status === "fulfilled" &&
          result.value.success &&
          result.value.url
        ) {
          const { latex, url, height, width, verticalAlign } = result.value;
          const latexContent = _.trim(latex.replace(/\$\$/g, ""));

          // * Create image tag with appropriate attributes and dimensions
          const imageTag = `<img src="${url}" alt="${sanitizeLatexText(
            latexContent
          )}" data-latex="${sanitizeLatexText(
            latexContent
          )}" height="${height}px" width="${width}px" style="vertical-align:${verticalAlign}px;display:inline;height:${height}px;width:${width}px;" />`.replace(
            /\s+/g,
            " "
          );

          // Replace the LaTeX with the image tag in the processed text
          processedText = processedText.replace(latex, imageTag);
        }
      });
    }
  } catch (error) {
    return text;
  }

  return processedText;
};

/**
 * Sanitizes instructionText <ul> and <ol> blocks by removing unnecessary whitespace and <br> tags.
 *
 * @param {string} html - The HTML string to sanitize.
 * @returns {string} - The sanitized HTML string.
 */
export const sanitizeWorksheetInstruction = async (
  content,
  descriptionField,
  updateAssessment,
  updateAssessmentInCache,
  uploadFileV2
) => {
  let instructionText = _.split(content, WS_INSTRUCTION_START_TAG);
  instructionText = _.split(_.last(instructionText), WS_INSTRUCTION_END_TAG);
  instructionText = _.trim(_.first(instructionText));

  // * if empty instruction return
  if (_.isEmpty(instructionText)) return;

  const fieldId = _.get(descriptionField, "id", "");
  const fieldUID = _.get(descriptionField, "uid", "");

  // * LOADER TEXT
  updateAssessmentInCache({
    fieldUID: fieldUID,
    params: {
      id: fieldId,
      value: "Generating worksheet instruction...",
      uid: fieldUID,
    },
    fieldObj: {
      __typename: "PlannerField",
      id: fieldId,
      uid: fieldUID,
      value: "Generating worksheet instruction...",
      resolvedMinimalTree: {
        id: fieldId,
        __typename: "ResolvedFieldUndefinedEntity",
        type: "UNDEFINED_ENTITY",
      },
    },
    extraParams: {},
  });

  // * Convert markdown to HTML
  instructionText = convertMdToHTML(instructionText, false);

  // * Replace LaTeX expressions with images
  instructionText = await replaceLatexWithImages(instructionText, uploadFileV2);

  // * Remove <br> tags and unnecessary whitespace from bullet points
  instructionText = _.replace(
    instructionText,
    /<(ul|ol)[^>]*>[\s\S]*?<\/\1>/gi,
    listBlock => {
      let sanitized = listBlock;

      // Remove line breaks/whitespace between </li> and <li>
      sanitized = _.replace(
        sanitized,
        /<\/li>(?:\s|<br\s*\/?>|[\r\n])+<li/gi,
        "</li><li"
      );

      // Remove <br> and whitespace after opening <ul>/<ol>
      sanitized = _.replace(
        sanitized,
        /(<(ul|ol)[^>]*>)(?:\s|<br\s*\/?>|[\r\n])+/gi,
        "$1"
      );

      // Remove <br> and whitespace before closing </ul>/<ol>
      sanitized = _.replace(
        sanitized,
        /(?:\s|<br\s*\/?>|[\r\n]+)+(<\/(ul|ol)>)/gi,
        "$1"
      );

      return sanitized;
    }
  );

  // * Updating description field in cache
  updateAssessmentInCache({
    fieldUID: fieldUID,
    params: {
      id: fieldId,
      value: instructionText,
      uid: fieldUID,
    },
    fieldObj: {
      __typename: "PlannerField",
      id: fieldId,
      uid: fieldUID,
      value: instructionText,
      resolvedMinimalTree: {
        id: fieldId,
        __typename: "ResolvedFieldUndefinedEntity",
        type: "UNDEFINED_ENTITY",
      },
    },
    extraParams: {},
  });

  // * Updating description field in assessment
  updateAssessment({
    id: _.get(descriptionField, "id"),
    value: instructionText,
    hasAddRemoveValues: false,
  });

  return instructionText;
};

/**
 * @deprecated Use `generateQuestionSequenceMapV2` instead.
 * This is kept for backward compatibility.
 * It will be removed in future versions.
 */
/**
 * Generates a question sequence map based on assessment questions and AI question mapping
 * @param {Object} assessmentQuestions - The getAssessmentQuestions response object
 * @param {Object} sequenceToQuestionIdMap - Map of AI question sequence to actual question IDs
 * @returns {Array} An array of question sequence mappings with questionId and questionSequence
 */
export const generateQuestionSequenceMap = (
  assessmentQuestions,
  sequenceToQuestionIdMap
) => {
  let questionSequence = 0;
  let aiQuestionSequence = 0;
  const questionSequenceMap = [];

  _.forEach(
    _.get(
      assessmentQuestions,
      "node.organizationResourcesV2.edges.0.node.childrenV2.edges",
      []
    ),
    section => {
      const questions = _.get(section, "node.childrenV2.edges", []);
      _.forEach(questions, question => {
        let questionId = _.get(question, "node.item.id");
        if (_.startsWith(questionId, AI_GEN_IDENTIFIER)) {
          aiQuestionSequence += 1;
          questionId = _.get(sequenceToQuestionIdMap, aiQuestionSequence);
        }
        if (questionId) {
          questionSequence += 1;
          questionSequenceMap.push({ questionId, questionSequence });
        }
      });
    }
  );

  return questionSequenceMap;
};

/**
 * Generates a mapping of question IDs to their sequence numbers based on the provided hierarchy.
 * Handles AI-generated questions by mapping their sequence using a provided map.
 *
 * @param {Array<Object>} sections - The hierarchical structure of sections containing questions.
 * @param {Object} sequenceToQuestionIdMap - A map of AI question sequence numbers to their corresponding question IDs.
 * @returns {Array<{questionId: string|number, questionSequence: number}>} An array of objects mapping question IDs to their sequence numbers.
 */
export const generateQuestionSequenceMapV2 = (
  sections,
  sequenceToQuestionIdMap
) => {
  let questionSequence = 0;
  let aiQuestionSequence = 0;
  const questionSequenceMap = [];

  _.forEach(sections, section => {
    const questions = _.get(section, "children", []);
    _.forEach(questions, question => {
      let questionId = _.get(question, "item.id");
      if (_.startsWith(questionId, AI_GEN_IDENTIFIER)) {
        aiQuestionSequence += 1;
        questionId = _.get(sequenceToQuestionIdMap, aiQuestionSequence);
      }
      if (questionId) {
        questionSequence += 1;
        questionSequenceMap.push({ questionId, questionSequence });
      }
    });
  });

  return questionSequenceMap;
};
